import { Component, EventEmitter, inject, Inject, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { interval, Subscription } from 'rxjs';
import { CommonService } from '../../../services/common/common.service';
import { Router } from '@angular/router';
import { ApiUrlHelper } from '../../../common/api-url-helper';
import { AuthenticationSuccessPopup } from '../../../component/authentication-success-popup/authentication-success-popup';
import { MatDialog } from '@angular/material/dialog';
import { CommonErrorMessages } from '../../../constants/ErrorMessages';
import { SharedService } from '../../../services/shared-service/shared.service';
import { PermissionStore } from '../../../services/permission-service/permission.store';

@Component({
  selector: 'app-verification-dialog',
  standalone: false,
  templateUrl: './verification-dialog.html',
  styleUrl: './verification-dialog.scss'
})

export class VerificationDialog {
  uniqueId: string;
  emailId: string;
  userId: number;
  otp: any;
  userDetails: any;
  submitted: boolean = false;
  verificationForm: FormGroup | any;
  isTimerRunning: boolean = false;
  isResendDisabled: boolean = true;
  resendCooldown: number = 30;
  countDown!: Subscription;
  private subscription: Subscription = new Subscription();
  private resendSubscription?: Subscription;
  counter = 301;
  tick = 1000;
  isDisabled: boolean = true;
  isCounterShow: boolean = true;
  interval: any;
  minutes: any = 5;
  seconds: any = 0;
  dialog = inject(MatDialog);
  private readonly permissionStore = inject(PermissionStore);
  @ViewChild('ngOtpInput', { static: false }) ngOtpInput: any;
  @Output() viewVerificationCode = new EventEmitter<boolean>();
  config = {
    allowNumbersOnly: true,
    length: 6,
    isPasswordInput: false,
    disableAutoFocus: false,
    placeholder: '',
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly dialogRef: MatDialogRef<VerificationDialog>,
    private readonly fb: FormBuilder,
    private readonly spinner: NgxSpinnerService,
    private readonly toastr: ToastrService,
    private readonly commonService: CommonService,
    private readonly router: Router,
    private readonly api: ApiUrlHelper,
    private readonly sharedService: SharedService
  ) {
    this.uniqueId = data.uniqueId;
    this.userId = data.userId;
    this.emailId = data.emailId;
  }

  ngOnInit(): void {
    this.verificationForm = this.fb.group({
     verificationCode: ['', [Validators.required]]
    });

    const storedEndTime = localStorage.getItem('otpEndTime');
    const now = new Date().getTime();

    if (storedEndTime && new Date(storedEndTime).getTime() <= now) {
      this.handleTimerEnd();

    } else {
      this.loadTimer();

    }

    this.startResendCooldownTimer(30);

    if (localStorage.getItem('userId')) {
      this.router.navigate(['/dashboard']);
    }
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.resendSubscription?.unsubscribe();
  }

  onOtpChange(event: any) {
    this.otp = event;
  }

  verifyCode() {
    this.submitted = true;

    if (!this.otp || this.otp.trim() === '') {
      this.submitted = false;
      this.toastr.error(CommonErrorMessages.AuthenticationCodeRequired);
      return;
    }

    const obj = {
      OTP: this.otp,
      Email: this.emailId,
      UniqueId: this.uniqueId
    };

    const apiUrl = this.api.apiUrl.Account.VerificationOTP;
    this.spinner.show();

    this.commonService.doPost(apiUrl, obj, { 'X-Auth-Email': this.emailId.toLowerCase() }).pipe().subscribe({
      next: (response) => {
        this.spinner.hide();
        if (response.success && response.data) {
          const tokenData = response.data;
          const currentDate = new Date().toISOString();

          this.sharedService.setValue('emailId', this.commonService.Encrypt(tokenData.emailId));
          this.sharedService.setValue('userId', this.commonService.Encrypt(tokenData.userId));
          this.sharedService.setValue('fullName', this.commonService.Encrypt(tokenData.firstName + ' ' + tokenData.lastName));
          this.sharedService.setValue('lastPasswordChange', this.commonService.Encrypt(tokenData.lastPasswordChange));
          this.sharedService.setValue('uniqueId', this.commonService.Encrypt(tokenData.uniqueId));
          this.sharedService.setValue('isFirstLogin', this.commonService.Encrypt(tokenData.isFirstLogin?.toString() ?? 'false'));
          this.permissionStore.set(tokenData.permissions);
          this.commonService.setCsrfToken(tokenData.csrfToken);
          this.dialogRef.close(true);

          if (tokenData.isFirstLogin) {
            this.router.navigate([
              `/reset-password/${this.commonService.Encrypt(tokenData.userId)}/${this.commonService.Encrypt(currentDate)}`
            ]);
            this.toastr.success(CommonErrorMessages.AuthenticationCodeVerifiedSuccessfully);
            this.sharedService.setValue('isFirstLogin', this.commonService.Encrypt(tokenData.isFirstLogin));
          } else {
            this.dialog.open(AuthenticationSuccessPopup, {
              width: '400px',
              disableClose: true
            });
          }
        } else {
          this.toastr.error(response.message || CommonErrorMessages.VerificationFailed);
          this.otp = '';
          this.ngOtpInput.setValue('');
          this.submitted = false;
        }
      },
      error: (err) => {
        console.error(err);
        this.spinner.hide();
        this.submitted = false;
      }
    });
  }

  ResendOTP() {
    if (this.isResendDisabled) return;

    this.startResendCooldownTimer(30);
    this.resetTimer();

    this.spinner.show();
    this.otp = '';
    this.ngOtpInput.setValue('');
    const apiUrl = this.api.apiUrl.Account.ResendOTP;

    const obj = {
      Email: this.emailId
    };

    this.commonService.doPost(apiUrl, obj, { 'X-Auth-Email': this.emailId.toLowerCase() }).pipe().subscribe({
      next: (response) => {
        this.spinner.hide();
        if (response.success) {
          this.submitted = false;
          if (response.data?.uniqueId) {
            this.uniqueId = response.data.uniqueId;
          }
          this.verificationForm.enable();
          this.toastr.success(response.message);
        } else {
          this.toastr.error(response.message);
        }
      },
      error: (err) => {
        console.error(err);
        this.spinner.hide();
      }
    });
  }

  startResendCooldownTimer(seconds: number = 30) {
    this.resendCooldown = seconds;
    this.isResendDisabled = true;
    this.resendSubscription?.unsubscribe();
    this.resendSubscription = interval(1000).subscribe(() => {
      if (this.resendCooldown <= 1) {
        this.resendCooldown = 0;
        this.isResendDisabled = false;
        this.resendSubscription?.unsubscribe();
      } else {
        this.resendCooldown--;
      }
    });
  }

  loadTimer() {
    const storedEndTime = localStorage.getItem('otpEndTime');
    if (!storedEndTime) {
      this.resetTimer();
      return;
    }

    const endTime = new Date(storedEndTime).getTime();
    const currentTime = new Date().getTime();
    const remainingTime = Math.floor((endTime - currentTime) / 1000);

    if (remainingTime <= 0) {
      this.handleTimerEnd();
    } else {
      this.counter = remainingTime;
      this.startTimer();
    }
  }

  saveTimer() {
    const endTime = new Date().getTime() + this.counter * 1000;
    localStorage.setItem('otpEndTime', new Date(endTime).toISOString());
  }

  resetTimer() {
    this.counter = 301;
    this.saveTimer();
    this.startTimer();
  }

  startTimer() {
    this.subscription?.unsubscribe();
    this.subscription = interval(1000).subscribe(() => {
      if (this.counter === 0) {
        this.handleTimerEnd();
        this.subscription?.unsubscribe();
      } else {
        this.counter--;
        this.minutes = Math.floor(this.counter / 60).toString().padStart(2, '0');
        this.seconds = (this.counter % 60).toString().padStart(2, '0');
        this.saveTimer();
      }
    });
  }

  handleTimerEnd() {
    this.subscription.unsubscribe();
    this.verificationForm.disable();
    this.submitted = true;
    localStorage.removeItem('otpEndTime');
  }

  close() {
    this.dialogRef.close(false);
  }
}